// ─────────────────────────────────────────────────────────────────────────────
// Dashboard page — Platform Dashboard. This component only wires things
// together:
//     • useDashboardData — the three independent fetches (KPI metrics, trend
//       series, 7-day session history) and everything derived from them
//     • refreshRef       — App.js hands this down so DaemonProvider can trigger
//       a re-fetch after the backend reconnects, without importing this page
//   All rendering lives in ./components; all formatting in ./format.js.
//
// Every panel is a <Card>: the glass shell, its three title treatments and the
// empty state are defined once there. A panel whose slice of the payload never
// arrived renders <EmptyState> — never a zero, which would read as a real
// measurement of nothing.
// ─────────────────────────────────────────────────────────────────────────────
import styles from "./Dashboard.module.css";
import { useDashboardData, BANDWIDTH_RANGES } from "./hooks/useDashboardData";
import DashboardHeader from "./components/DashboardHeader";
import StatsRow from "./components/StatsRow";
import Card, { EmptyState } from "./components/Card";
import DonutChart from "./components/DonutChart";
import ActivityChart from "./components/ActivityChart";
import BandwidthChart from "./components/BandwidthChart";
import AdminActions from "./components/AdminActions";
import AlertsList, { CriticalBadge } from "./components/AlertsList";
import RangeSelect from "./components/RangeSelect";

export default function Dashboard({ refreshRef }) {
  const {
    metrics, range, setRange,
    donutData, sessionHistory, adminActions,
    alerts, criticalCount, bandwidth,
  } = useDashboardData(refreshRef);

  return (
    <div className={styles.page}>
      <DashboardHeader />

      {/* ── Row 1: KPI tiles ── */}
      <StatsRow metrics={metrics} />

      {/* ── Row 2: traffic · activity · admin actions ── */}
      <div className={styles.midRow}>
        <Card title="Traffic by Region" variant="retro">
          {donutData.length > 0
            ? <DonutChart data={donutData} />
            : <EmptyState>No traffic data</EmptyState>}
        </Card>

        <Card title="User Activity Trend" variant="retro">
          {sessionHistory
            ? <ActivityChart historyData={sessionHistory} />
            : <EmptyState>No trend data</EmptyState>}
        </Card>

        <Card title="Recent Admin Actions" variant="ui">
          {adminActions.length > 0
            ? <AdminActions actions={adminActions} />
            : <EmptyState>No recent actions</EmptyState>}
        </Card>
      </div>

      {/* ── Row 3: alerts · bandwidth ── */}
      <div className={styles.bottomRow}>
        <Card title="Recent Alerts" action={<CriticalBadge count={criticalCount} />}>
          {alerts.length > 0
            ? <AlertsList alerts={alerts} />
            : <EmptyState>No alerts</EmptyState>}
        </Card>

        <Card
          title="Network Bandwidth"
          wide
          action={<RangeSelect value={range} options={BANDWIDTH_RANGES} onChange={setRange} />}
        >
          {bandwidth
            ? <BandwidthChart labels={bandwidth.labels} values={bandwidth.values} isHourly={bandwidth.isHourly} />
            : <EmptyState>No bandwidth data</EmptyState>}
        </Card>
      </div>
    </div>
  );
}
